/**
 * Safe localStorage utilities with error handling
 */

/**
 * Get value from localStorage with error handling
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if key doesn't exist or error occurs
 * @returns {*} Stored value or default
 */
export const getFromStorage = (key, defaultValue = null) => {
  let item;
  try {
    item = window.localStorage.getItem(key);
  } catch (error) {
    console.warn(`Error reading from localStorage (key: ${key}):`, error);
    return defaultValue;
  }
  // Missing or empty value → default (preserves prior falsy-item behavior).
  if (item === null || item === '') return defaultValue;
  try {
    return JSON.parse(item);
  } catch {
    // Not valid JSON — e.g. a raw string like a language code ("cs"), a plain
    // number/boolean, or a value written by an older build. Return it as-is so
    // the caller still gets the stored value instead of silently discarding it
    // (this previously dropped the user's saved language preference on reload).
    return item;
  }
};

/**
 * Set value in localStorage with error handling
 * @param {string} key - Storage key
 * @param {*} value - Value to store (will be JSON stringified)
 * @returns {boolean} Success status
 */
export const setInStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Error writing to localStorage (key: ${key}):`, error);
    return false;
  }
};

/**
 * Remove value from localStorage with error handling
 * @param {string} key - Storage key
 * @returns {boolean} Success status
 */
export const removeFromStorage = (key) => {
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Error removing from localStorage (key: ${key}):`, error);
    return false;
  }
};
